import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Lock, Mail, Phone, Trash2, Home, RefreshCw, LogOut, MapPin, FileText } from "lucide-react";
import { toast, Toaster } from "sonner";

const ADMIN_KEY = "lasala_admin_pwd";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password });
      sessionStorage.setItem(ADMIN_KEY, password);
      onLogin(password);
    } catch {
      toast.error("Password errata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <Toaster position="top-right" richColors />
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-[#E8DFD8] rounded-[2rem] p-10 shadow-sm" data-testid="admin-login-form">
        <div className="w-14 h-14 rounded-full bg-[#F3E6DA] flex items-center justify-center mb-6">
          <Lock size={20} className="text-[#8B5A3C]" />
        </div>
        <h1 className="font-display text-3xl text-[#2C2621] mb-2">Area riservata</h1>
        <p className="text-sm text-[#5C544B] mb-7">Inserisci la password admin per consultare le richieste.</p>
        <input
          type="password"
          autoFocus
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password admin"
          data-testid="admin-password-input"
          className="w-full bg-[#FDFBF7] border border-[#E8DFD8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]/30 focus:border-[#8B5A3C] mb-5"
        />
        <button
          disabled={loading}
          type="submit"
          data-testid="admin-login-submit"
          className="w-full bg-[#8B5A3C] text-white hover:bg-[#704830] disabled:opacity-60 transition-colors rounded-full py-3 font-medium"
        >
          {loading ? "…" : "Accedi"}
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ password, onLogout }) => {
  const [tab, setTab] = useState("evaluations");
  const [evaluations, setEvaluations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const headers = { "X-Admin-Password": password };

  const load = async () => {
    setLoading(true);
    try {
      const [ev, ct] = await Promise.all([
        api.get("/admin/evaluations", { headers }),
        api.get("/admin/contacts", { headers }),
      ]);
      setEvaluations(ev.data);
      setContacts(ct.data);
    } catch {
      toast.error("Errore caricamento dati");
      onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (kind, id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await api.delete(`/admin/${kind}/${id}`, { headers });
      toast.success("Eliminata");
      setSelected(null);
      load();
    } catch {
      toast.error("Errore");
    }
  };

  const items = tab === "evaluations" ? evaluations : contacts;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12" data-testid="admin-dashboard">
      <Toaster position="top-right" richColors />
      <div className="flex flex-wrap gap-4 items-center justify-between mb-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#8B5A3C] mb-2">Area riservata</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#2C2621]">Le tue richieste</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="border border-[#E8DFD8] hover:border-[#8B5A3C] rounded-full px-5 py-2.5 text-sm flex items-center gap-2 transition-colors" data-testid="admin-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
          <button onClick={onLogout} className="border border-[#E8DFD8] hover:border-[#8B5A3C] hover:text-[#8B5A3C] rounded-full px-5 py-2.5 text-sm flex items-center gap-2 transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E8DFD8] mb-8" data-testid="admin-tabs">
        <button
          onClick={() => { setTab("evaluations"); setSelected(null); }}
          data-testid="tab-evaluations"
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === "evaluations" ? "border-[#8B5A3C] text-[#8B5A3C]" : "border-transparent text-[#5C544B] hover:text-[#2C2621]"}`}
        >
          Valutazioni <span className="ml-1.5 bg-[#F3E6DA] text-[#8B5A3C] text-[11px] font-bold px-2 py-0.5 rounded-full">{evaluations.length}</span>
        </button>
        <button
          onClick={() => { setTab("contacts"); setSelected(null); }}
          data-testid="tab-contacts"
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === "contacts" ? "border-[#8B5A3C] text-[#8B5A3C]" : "border-transparent text-[#5C544B] hover:text-[#2C2621]"}`}
        >
          Messaggi <span className="ml-1.5 bg-[#F3E6DA] text-[#8B5A3C] text-[11px] font-bold px-2 py-0.5 rounded-full">{contacts.length}</span>
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 text-[#8A8178]">Nessuna richiesta al momento.</div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelected(it)}
                data-testid={`admin-item-${it.id}`}
                className={`w-full text-left bg-white border rounded-2xl p-5 transition-all ${selected?.id === it.id ? "border-[#8B5A3C] shadow-md" : "border-[#E8DFD8] hover:border-[#8B5A3C]"}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-display text-lg text-[#2C2621]">{it.name}</div>
                  <div className="text-[11px] text-[#8A8178]">{fmtDate(it.created_at)}</div>
                </div>
                <div className="text-xs text-[#5C544B] flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Mail size={11} /> {it.email}</span>
                  {it.phone && <span className="flex items-center gap-1"><Phone size={11} /> {it.phone}</span>}
                </div>
                {tab === "evaluations" && (
                  <div className="text-xs text-[#5C544B] mt-1.5 flex items-center gap-1"><MapPin size={11} className="text-[#8B5A3C]" /> {it.address}</div>
                )}
                {tab === "contacts" && it.message && (
                  <div className="text-xs text-[#5C544B] mt-1.5 line-clamp-1">{it.message}</div>
                )}
              </button>
            ))}
          </div>

          <div className="lg:col-span-3">
            {selected ? (
              <div className="bg-white border border-[#E8DFD8] rounded-2xl p-7 lg:sticky lg:top-28" data-testid="admin-detail">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="font-display text-2xl text-[#2C2621]">{selected.name}</h3>
                    <div className="text-xs text-[#8A8178] mt-1">{fmtDate(selected.created_at)}</div>
                  </div>
                  <button
                    onClick={() => remove(tab, selected.id)}
                    className="p-2 text-[#8A8178] hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    data-testid="admin-delete"
                    aria-label="Elimina"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <dl className="space-y-4 text-sm">
                  <Row icon={Mail} label="Email" value={<a className="text-[#8B5A3C] hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a>} />
                  <Row icon={Phone} label="Telefono" value={selected.phone ? <a className="text-[#8B5A3C] hover:underline" href={`tel:${selected.phone}`}>{selected.phone}</a> : "-"} />
                  {tab === "evaluations" ? (
                    <>
                      <Row icon={Home} label="Tipo immobile" value={selected.property_type} />
                      <Row icon={MapPin} label="Indirizzo" value={selected.address} />
                      <Row label="Superficie" value={selected.area_sqm ? `${selected.area_sqm} mq` : "-"} />
                      <Row label="Vani" value={selected.rooms || "-"} />
                      <Row icon={FileText} label="Note" value={selected.notes || "-"} />
                    </>
                  ) : (
                    <>
                      {selected.property_id && <Row icon={Home} label="Immobile (id)" value={selected.property_id} />}
                      <Row icon={FileText} label="Messaggio" value={<span className="whitespace-pre-line">{selected.message}</span>} />
                    </>
                  )}
                </dl>
              </div>
            ) : (
              <div className="bg-[#F3E6DA] rounded-2xl p-10 text-center text-[#8A8178]">
                Seleziona una richiesta a sinistra per vederne i dettagli.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3 items-start">
    <div className="w-9 h-9 rounded-full bg-[#F3E6DA] flex items-center justify-center shrink-0">
      {Icon ? <Icon size={14} className="text-[#8B5A3C]" /> : <span className="w-2 h-2 bg-[#8B5A3C] rounded-full" />}
    </div>
    <div className="flex-1 pt-1.5">
      <dt className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A8178] mb-0.5">{label}</dt>
      <dd className="text-[#2C2621]">{value}</dd>
    </div>
  </div>
);

const AdminPage = () => {
  const [password, setPassword] = useState(() => sessionStorage.getItem(ADMIN_KEY) || "");

  const logout = () => {
    sessionStorage.removeItem(ADMIN_KEY);
    setPassword("");
  };

  if (!password) return <Login onLogin={setPassword} />;
  return <Dashboard password={password} onLogout={logout} />;
};

export default AdminPage;
